import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpsertApplePayIntegrator } from '@/lib/api/apple-pay-platform';

export interface IntegratorFormValues {
  ppiid: string;
  label: string;
  btMerchantRegistrationId: string | null;
  associationFileContent: string | null;
}

/**
 * The platform integrator fields, shared by first-time setup and later edits.
 *
 * The association file is write-only: the stored value is never rendered back
 * into the textarea, so leaving it blank means "keep the current file" rather
 * than "clear it". It's a 228-character opaque blob — echoing it would invite
 * an accidental partial edit that silently deverifies every domain. Read it
 * through `AssociationFileDialog` instead.
 *
 * There is deliberately no merchant-identifier field here. Apple requires one
 * identifier per merchant, so it is derived per domain from the account —
 * a platform-wide value would register every merchant as a single merchant.
 */
export function IntegratorForm({
  environment,
  initial,
  onSaved,
  submitLabel,
}: {
  environment: 'production' | 'sandbox';
  initial?: IntegratorFormValues;
  onSaved?: () => void;
  submitLabel: string;
}) {
  const { t } = useTranslation('manageApplePay');
  const upsert = useUpsertApplePayIntegrator();

  const [ppiid, setPpiid] = useState(initial?.ppiid ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [btRegistrationId, setBtRegistrationId] = useState(initial?.btMerchantRegistrationId ?? '');
  const [associationFile, setAssociationFile] = useState('');

  const hasStoredFile = !!initial?.associationFileContent;
  const canSubmit =
    !!ppiid.trim() &&
    !!label.trim() &&
    (hasStoredFile || !!associationFile.trim()) &&
    !upsert.isPending;

  const submit = () => {
    upsert.mutate(
      {
        environment,
        ppiid: ppiid.trim(),
        label: label.trim(),
        ...(associationFile.trim() ? { associationFileContent: associationFile.trim() } : {}),
        ...(btRegistrationId.trim() ? { btMerchantRegistrationId: btRegistrationId.trim() } : {}),
      },
      { onSuccess: () => onSaved?.() },
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="ppiid">{t('integrator.ppiid')}</Label>
          <Input
            id="ppiid"
            value={ppiid}
            onChange={(e) => setPpiid(e.target.value)}
            placeholder="platformintegrator.com.example"
            className="font-mono text-sm"
          />
          <p className="text-muted-foreground text-xs">{t('integrator.ppiidHint')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ppiid-label">{t('integrator.label')}</Label>
          <Input id="ppiid-label" value={label} onChange={(e) => setLabel(e.target.value)} />
          <p className="text-muted-foreground text-xs">{t('integrator.labelHint')}</p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="bt-registration">{t('integrator.btRegistration')}</Label>
        <Input
          id="bt-registration"
          value={btRegistrationId}
          onChange={(e) => setBtRegistrationId(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          className="font-mono text-sm"
        />
        <p className="text-muted-foreground text-xs">{t('integrator.btRegistrationHint')}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="association-file">{t('integrator.associationFile')}</Label>
        <Textarea
          id="association-file"
          value={associationFile}
          onChange={(e) => setAssociationFile(e.target.value)}
          rows={4}
          className="font-mono text-xs"
          placeholder={hasStoredFile ? t('integrator.associationFileKeep') : undefined}
        />
        <p className="text-muted-foreground text-xs">{t('integrator.associationFileHint')}</p>
        {hasStoredFile && !associationFile.trim() && (
          <p className="flex items-center gap-1.5 text-xs text-emerald-600">
            <Check className="h-3.5 w-3.5" />
            {t('integrator.associationFileKeep')}
          </p>
        )}
      </div>

      <Button disabled={!canSubmit} onClick={submit}>
        {submitLabel}
      </Button>
    </div>
  );
}
